import {
  Music,
  Trophy,
  UtensilsCrossed,
  Laptop,
  Palette,
  Wine,
  Calendar,
  Utensils,
  Cpu,
  Briefcase,
  Users,
  HeartPulse,
  Puzzle,
  type LucideIcon,
} from 'lucide-react';

/**
 * Maps category iconName (from database) to Lucide React icon component
 *
 * Supports multiple icon name formats:
 * - camelCase: "music", "trophy", "utensils"
 * - PascalCase: "Music", "Trophy", "Utensils"
 * - kebab-case: "utensils-crossed"
 *
 * Fallback: Returns Calendar icon if iconName is not found
 */
export const categoryIconMap: Record<string, LucideIcon> = {
  // Music category
  music: Music,
  Music: Music,

  // Sports category
  sports: Trophy,
  trophy: Trophy,
  Trophy: Trophy,

  // Food category
  food: UtensilsCrossed,
  utensils: Utensils, // Basic utensils icon
  'utensils-crossed': UtensilsCrossed,
  UtensilsCrossed: UtensilsCrossed,
  Utensils: Utensils,

  // Science & Tech category (`tech` retired → `science-tech`, COR-37)
  tech: Laptop,
  laptop: Laptop,
  Laptop: Laptop,
  'science-tech': Cpu,
  cpu: Cpu,
  Cpu: Cpu,

  // Art category
  art: Palette,
  palette: Palette,
  Palette: Palette,

  // Drinks category
  drinks: Wine,
  wine: Wine,
  Wine: Wine,

  // Business & Networking category (WEB-606)
  'business-networking': Briefcase,
  briefcase: Briefcase,
  Briefcase: Briefcase,

  // Community & Culture category (WEB-606)
  'community-culture': Users,
  users: Users,
  Users: Users,

  // Wellness category (WEB-606)
  wellness: HeartPulse,
  'heart-pulse': HeartPulse,
  HeartPulse: HeartPulse,

  // Hobbies & Interests category (WEB-606)
  'hobbies-interests': Puzzle,
  puzzle: Puzzle,
  Puzzle: Puzzle,

  // Default fallback
  calendar: Calendar,
  Calendar: Calendar,
};

/**
 * Get Lucide icon component for a category iconName
 *
 * @param iconName - Icon name from database (e.g., "music", "utensils")
 * @returns Lucide icon component, defaults to Calendar if not found
 *
 * @example
 * const IconComponent = getCategoryIcon('music');
 * <IconComponent size={24} color="#FF6B35" />
 */
export function getCategoryIcon(iconName: string | null | undefined): LucideIcon {
  if (!iconName) {
    return Calendar;
  }

  return categoryIconMap[iconName] || Calendar;
}

/**
 * Get icon component with type safety
 *
 * @param iconName - Icon name from database
 * @returns Object with icon component and isDefault flag
 *
 * @example
 * const { icon: Icon, isDefault } = getCategoryIconWithFallback('music');
 * if (isDefault) {
 *   console.warn('Using default icon for category');
 * }
 */
export function getCategoryIconWithFallback(iconName: string | null | undefined): {
  icon: LucideIcon;
  isDefault: boolean;
} {
  const icon = getCategoryIcon(iconName);
  const isDefault = icon === Calendar;

  return { icon, isDefault };
}
